import { useQuery } from '@tanstack/react-query'

import {
  spendReportsServiceKey,
  getOverviewService,
  getTotalSpendService,
  getDailySpendService,
  getHourlySpendService,
  getCumulativeDailyService
} from '../services/campaign/spend-reports'
import {
  fibonacciRetryDelay,
  mapTotalSpend,
  mapDailySpend,
  mapHourlySpend,
  mapCumulativeDaily,
  isNoSpendDataError,
  isNotFoundError
} from './useSpendReports.helpers'

export { isNoSpendDataError, isNotFoundError } from './useSpendReports.helpers'

export const useSpendOverview = (
  campaignId: string,
  params: { start_date: string; end_date: string }
) => {
  const queryKey = [spendReportsServiceKey, 'overview', campaignId, params.start_date, params.end_date]

  const { data, isPending, isFetching, error, refetch } = useQuery({
    enabled: !!campaignId && !!params.start_date,
    queryKey,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, err) => !isNoSpendDataError(err) && !isNotFoundError(err) && failureCount < 5,
    retryDelay: fibonacciRetryDelay,
    queryFn: async () => {
      const response = await getOverviewService(campaignId, params)
      return response.data
    },
    placeholderData: previousData => previousData
  })

  return { data, isPending, isFetching, error, refetch }
}

export const useTotalSpend = (
  campaignId: string,
  params: { start_date: string; end_date: string },
  enabled = true,
  campaignEndAt?: string
) => {
  const queryKey = [spendReportsServiceKey, 'total-spend', campaignId, params.start_date, params.end_date, campaignEndAt ?? '']

  const { data, isPending, isFetching, error, refetch } = useQuery({
    enabled: !!campaignId && enabled,
    queryKey,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, err) => !isNoSpendDataError(err) && failureCount < 5,
    retryDelay: fibonacciRetryDelay,
    queryFn: async () => {
      const response = await getTotalSpendService(campaignId, params)
      return mapTotalSpend(response.data, campaignEndAt)
    },
    placeholderData: previousData => previousData
  })

  return { data, isPending, isFetching, error, refetch }
}

export const useDailySpend = (
  campaignId: string,
  params: { start_date: string; end_date: string },
  enabled = true,
  campaignEndAt?: string
) => {
  const queryKey = [spendReportsServiceKey, 'daily-spend', campaignId, params.start_date, params.end_date, campaignEndAt ?? '']

  const { data, isPending, isFetching, error, refetch } = useQuery({
    enabled: !!campaignId && enabled,
    queryKey,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, err) => !isNoSpendDataError(err) && failureCount < 5,
    retryDelay: fibonacciRetryDelay,
    queryFn: async () => {
      const response = await getDailySpendService(campaignId, params)
      return mapDailySpend(response.data, campaignEndAt)
    },
    placeholderData: previousData => previousData
  })

  return { data, isPending, isFetching, error, refetch }
}

export const useHourlySpend = (
  campaignId: string,
  params: { date: string },
  enabled = true
) => {
  const queryKey = [spendReportsServiceKey, 'hourly-spend', campaignId, params.date]

  const { data, isPending, isFetching, error, refetch } = useQuery({
    enabled: !!campaignId && enabled,
    queryKey,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (failureCount, err) => !isNoSpendDataError(err) && failureCount < 5,
    retryDelay: fibonacciRetryDelay,
    queryFn: async () => {
      const response = await getHourlySpendService(campaignId, params)
      return mapHourlySpend(response.data, params.date)
    }
  })

  return { data, isPending, isFetching, error, refetch }
}

export const useCumulativeDaily = (
  campaignId: string,
  params: { date: string },
  enabled = true
) => {
  const queryKey = [spendReportsServiceKey, 'cumulative-daily', campaignId, params.date]

  const { data, isPending, isFetching, error, refetch } = useQuery({
    enabled: !!campaignId && enabled,
    queryKey,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (failureCount, err) => !isNoSpendDataError(err) && failureCount < 5,
    retryDelay: fibonacciRetryDelay,
    queryFn: async () => {
      const response = await getCumulativeDailyService(campaignId, params)
      return mapCumulativeDaily(response.data, params.date)
    }
  })

  return { data, isPending, isFetching, error, refetch }
}
